import { usePersistedState } from "@/lib/persistedFilters";
// Templates panel — reusable script-template library with fuzzy search,
// visibility filter tabs, per-tab match counts, preview pane, and an editor
// dialog that respects the seed-template content-lock.
//
// Extracted from AIOutbound.jsx during the P1 refactor.

import { useEffect, useState } from "react";
import {
  Copy, Pencil, Trash2, X, Search, Eye, Check, Loader2, GitFork,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  TEMPLATE_VISIBILITY_TABS, VISIBILITY_LABELS, EMPTY_SCRIPT,
  Row2, Field, LabelWithHint,
} from "./shared";

export function TemplatesPanel() {
  const { user } = useAuth();
  const isStaff = ((user?.base_role || user?.role) || "").toLowerCase() === "staff";
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // template being previewed
  const [editing, setEditing] = useState(null);     // template being edited (own copy)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [forkTarget, setForkTarget] = useState(null);   // template being forked to an org
  const [visFilter, setVisFilter] = usePersistedState("ai-outbound-templates.vis", "all"); // client-side filter tab
  const [query, setQuery] = usePersistedState("ai-outbound-templates.q", "");            // fuzzy search box

  // Match query against name / description / tags (case-insensitive substring).
  const matches = (t) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const hay = [
      t.name || "",
      t.description || "",
      ...(Array.isArray(t.tags) ? t.tags : []),
    ].join(" ").toLowerCase();
    return hay.includes(needle);
  };
  const searched = templates.filter(matches);
  const filtered = searched.filter((t) => visFilter === "all" ? true : t.visibility === visFilter);

  // Per-tab match counts so users can see where their hits live without switching tabs.
  const tabCounts = TEMPLATE_VISIBILITY_TABS.reduce((acc, tab) => {
    acc[tab.value] = tab.value === "all"
      ? searched.length
      : searched.filter((t) => t.visibility === tab.value).length;
    return acc;
  }, {});

  const onQueryChange = (v) => {
    // When the user starts typing from an empty box, jump to "All" so hits
    // aren't hidden behind the current visibility tab. Once they've begun a
    // search we leave their tab choice alone so they can drill in.
    if (v && !query && visFilter !== "all") setVisFilter("all");
    setQuery(v);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outbound/script-templates");
      const rows = data.templates || [];
      setTemplates(rows);
      // Keep the current selection in sync (or pick first)
      if (selected) {
        const still = rows.find((r) => r.template_id === selected.template_id);
        setSelected(still || rows[0] || null);
      } else if (rows.length) setSelected(rows[0]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const duplicate = async (t) => {
    try {
      const { data } = await api.post("/outbound/script-templates", {
        name: `${t.name} (copy)`,
        description: t.description || "",
        script: t.script || EMPTY_SCRIPT(),
        tags: t.tags || [],
        calendly_link: t.calendly_link || "",
      });
      toast.success(`Duplicated as "${data.name}"`);
      await load();
      setSelected(data);
      return data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };
  const copyToOrg = async (t) => {
    // Duplicate the built-in and jump straight into the edit dialog so the user
    // doesn't have to scan the list and click Edit as a separate step.
    const copy = await duplicate(t);
    if (copy) setEditing(copy);
  };
  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/outbound/script-templates/${deleteTarget.template_id}`);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-slate-400" data-testid="ai-outbound-templates-loading">Loading templates…</div>;

  const filterStrip = (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-xs" data-testid="ai-outbound-template-search-wrap">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" aria-hidden />
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search templates by name, description, or tag…"
          className="h-8 pl-8 pr-8 text-xs"
          data-testid="ai-outbound-template-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange("")}
            data-testid="ai-outbound-template-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            title="Clear search">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white" data-testid="ai-outbound-template-visibility-filter">
        {TEMPLATE_VISIBILITY_TABS.map((t) => {
          const active = visFilter === t.value;
          const count = tabCounts[t.value] ?? 0;
          return (
            <button key={t.value} onClick={() => setVisFilter(t.value)}
                    data-testid={`ai-outbound-template-visibility-filter-${t.value}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "text-slate-600 hover:text-slate-800"
                    }`}>
              <span>{t.label}</span>
              <span
                data-testid={`ai-outbound-template-visibility-count-${t.value}`}
                className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                  active ? "bg-white/25 text-primary-foreground" : "bg-slate-100 text-slate-500"
                }`}>{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-5" data-testid="ai-outbound-templates-panel">
      {/* Left: table of templates */}
      <div className="md:col-span-3 min-w-0">
        {filterStrip}
        <Card className="border border-slate-200 p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Visibility</th>
                <th className="px-3 py-2 text-left hidden 2xl:table-cell">Tags</th>
                <th className="px-3 py-2 text-left hidden lg:table-cell">Author</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-slate-400" data-testid="ai-outbound-templates-empty">
                  {templates.length === 0
                    ? <>No templates yet. Build a campaign script and click <span className="font-medium text-slate-600">Save as new template</span> to reuse it here.</>
                    : query.trim()
                      ? <>No templates match <span className="font-medium text-slate-600">&ldquo;{query}&rdquo;</span>. Try a different keyword or <button type="button" onClick={() => onQueryChange("")} className="font-medium text-primary hover:underline" data-testid="ai-outbound-templates-empty-clear">clear the search</button>.</>
                      : <>No templates in this visibility slice. Switch to <span className="font-medium text-slate-600">All</span> to see everything you have access to.</>}
                </td></tr>
              )}
              {filtered.map((t) => {
                const isSelected = selected?.template_id === t.template_id;
                const vis = VISIBILITY_LABELS[t.visibility] || VISIBILITY_LABELS.staff;
                return (
                  <tr key={t.template_id} className={`cursor-pointer ${isSelected ? "bg-primary/5" : "hover:bg-slate-50"}`}
                      onClick={() => setSelected(t)} data-testid={`ai-outbound-template-row-${t.template_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {t.name}
                        {t.is_seed && (
                          <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] font-medium text-blue-700">Built-in</span>
                        )}
                      </div>
                      {t.description && <div className="text-xs text-slate-400 line-clamp-1">{t.description}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-1.5 py-0 text-[10px] font-medium ${vis.cls}`}
                            data-testid={`ai-outbound-template-visibility-${t.template_id}`}>{vis.text}</span>
                    </td>
                    <td className="px-3 py-2 hidden 2xl:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(t.tags || []).slice(0, 3).map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0 text-[10px] text-slate-600">{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 hidden lg:table-cell">{t.is_seed ? "System" : (t.created_by_name || "—")}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {t.is_seed ? (
                        <div className="flex flex-col items-end gap-1 xl:flex-row xl:items-center xl:justify-end">
                          <button onClick={(e) => { e.stopPropagation(); setEditing(t); }}
                                  data-testid={`ai-outbound-template-edit-${t.template_id}`}
                                  className="whitespace-nowrap rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                                  title={isStaff
                                    ? "Edit this built-in template. Your changes persist across restarts."
                                    : "Change who can see this built-in template"}>
                            {isStaff ? "Edit built-in" : "Set visibility"}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setForkTarget(t); }}
                                  data-testid={`ai-outbound-template-copy-to-org-${t.template_id}`}
                                  className="whitespace-nowrap rounded-md border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
                                  title={isStaff ? "Copy this built-in template into any organization's library" : "Copy this built-in template into an editable copy in your org's library"}>
                            <Copy className="mr-1 inline h-3 w-3" />
                            Copy to org
                          </button>
                        </div>
                      ) : (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); duplicate(t); }}
                                  data-testid={`ai-outbound-template-duplicate-${t.template_id}`}
                                  className="mr-2 text-slate-300 hover:text-primary" title="Duplicate">
                            <Copy className="h-4 w-4" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setEditing(t); }}
                                  data-testid={`ai-outbound-template-edit-${t.template_id}`}
                                  className="mr-2 text-slate-300 hover:text-primary" title="Edit">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }}
                                  data-testid={`ai-outbound-template-delete-${t.template_id}`}
                                  className="text-slate-300 hover:text-red-500" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Right: preview */}
      <div className="md:col-span-2">
        <TemplatePreview template={selected} />
      </div>

      {editing && (
        <TemplateEditDialog template={editing} open={!!editing} onClose={() => setEditing(null)}
          isStaff={isStaff}
          onSaved={async () => { setEditing(null); await load(); }} />
      )}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="ai-outbound-template-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete this template?</DialogTitle>
            <DialogDescription>
              &quot;{deleteTarget?.name}&quot; will be removed for everyone in your organisation. Existing campaigns that used this template as a starting point are unaffected — their scripts are copies. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="ai-outbound-template-delete-cancel">Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="ai-outbound-template-delete-confirm">
              <Trash2 className="h-4 w-4" /> Delete template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ForkToOrgDialog
        template={forkTarget}
        open={!!forkTarget}
        onClose={() => setForkTarget(null)}
        isStaff={isStaff}
        onDone={(newDoc) => { setTemplates((cur) => [newDoc, ...cur]); setForkTarget(null); }}
      />
    </div>
  );
}

function ForkToOrgDialog({ template, open, onClose, isStaff, onDone }) {
  const [orgs, setOrgs] = useState(null);
  const [targetOrgId, setTargetOrgId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTargetOrgId("");
    setOrgs(null);
    if (!isStaff) return;  // Non-staff users always land in their own org (backend enforces).
    (async () => {
      try {
        // Pull both partner and customer orgs so staff can pick any.
        const [{ data: p }, { data: c }] = await Promise.all([
          api.get("/organizations?type=partner"),
          api.get("/organizations?type=customer"),
        ]);
        const combined = [...(p.organizations || p || []), ...(c.organizations || c || [])]
          .map((o) => ({ org_id: o.org_id, name: o.name, type: o.type }));
        combined.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(combined);
      } catch {
        setOrgs([]);
      }
    })();
  }, [open, isStaff]);

  const copyToOrg = async () => {
    if (!template) return;
    if (isStaff && !targetOrgId) { toast.error("Pick an organization to copy into"); return; }
    setSaving(true);
    try {
      const payload = {
        name: `${template.name} (copy)`,
        description: template.description || "",
        script: template.script || EMPTY_SCRIPT,
        tags: template.tags || [],
        calendly_link: template.calendly_link || "",
        visibility: "org",   // copies default to org-visible so the target org can find them
        target_org_id: isStaff ? targetOrgId : "",
      };
      const { data } = await api.post("/outbound/script-templates", payload);
      const orgName = orgs?.find((o) => o.org_id === targetOrgId)?.name || (isStaff ? targetOrgId : "your organization");
      toast.success(`Copied "${template.name}" into ${orgName}`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="ai-outbound-template-fork-dialog">
        <DialogHeader>
          <DialogTitle>Copy template to organization</DialogTitle>
          <DialogDescription>
            &ldquo;{template?.name}&rdquo; will be copied as an editable, non-seed template inside the target org&apos;s library.
          </DialogDescription>
        </DialogHeader>
        {isStaff ? (
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">Target organization</label>
            {orgs === null ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading organizations…
              </div>
            ) : orgs.length === 0 ? (
              <div className="text-xs text-red-600">No organizations available to copy into.</div>
            ) : (
              <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                <SelectTrigger data-testid="ai-outbound-template-fork-org-select">
                  <SelectValue placeholder="Pick a partner or customer…" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.org_id} value={o.org_id}>
                      <span className="mr-1 rounded-full bg-slate-100 px-1.5 text-[10px] font-medium uppercase text-slate-500">{o.type}</span>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500">
            This template will be copied into your organization&#39;s library.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-template-fork-cancel">Cancel</Button>
          <Button onClick={copyToOrg} disabled={saving || (isStaff && !targetOrgId)}
                  data-testid="ai-outbound-template-fork-confirm">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            Copy template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePreview({ template }) {
  if (!template) {
    return (
      <Card className="border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400"
            data-testid="ai-outbound-template-preview-empty">
        <Eye className="mx-auto h-6 w-6 text-slate-300" />
        <div className="mt-2 font-medium text-slate-500">Select a template to preview</div>
      </Card>
    );
  }
  const s = template.script || {};
  const renderSection = (label, value) => value ? (
    <div key={label}>
      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
      <div className="mt-0.5 text-xs text-slate-700 whitespace-pre-wrap">{value}</div>
    </div>
  ) : null;
  return (
    <Card className="border border-slate-200 p-4 space-y-3" data-testid="ai-outbound-template-preview">
      <div>
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          {template.name}
          {template.is_seed && (
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] font-medium text-blue-700">Built-in</span>
          )}
        </div>
        {template.description && <p className="mt-0.5 text-xs text-slate-500">{template.description}</p>}
      </div>
      <div className="space-y-2 border-t border-slate-100 pt-3">
        {renderSection("Opening (hook)", s.opening)}
        {renderSection("Value prop", s.value_prop)}
        {renderSection("Qualifying question", s.qualifying_question)}
        {(s.value_bullets || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Value bullets</div>
            <ul className="mt-0.5 list-disc pl-4 text-xs text-slate-700 space-y-0.5">
              {s.value_bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}
        {renderSection("Call to action", s.call_to_action)}
        {(s.objections || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Objection handling</div>
            <div className="mt-0.5 space-y-1">
              {s.objections.map((o, i) => (
                <div key={i} className="rounded border border-slate-100 bg-slate-50 p-1.5 text-xs">
                  <div className="font-medium text-slate-700">{o.trigger}</div>
                  <div className="text-slate-600 mt-0.5 whitespace-pre-wrap">{o.response}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {renderSection("Close", s.close)}
      </div>
    </Card>
  );
}

function TemplateEditDialog({ template, open, onClose, onSaved, isStaff }) {
  const [name, setName] = useState(template.name || "");
  const [description, setDescription] = useState(template.description || "");
  const [tagsStr, setTagsStr] = useState((template.tags || []).join(", "));
  const [calendlyLink, setCalendlyLink] = useState(template.calendly_link || "");
  const [visibility, setVisibility] = useState(template.visibility || "staff");
  const [script, setScript] = useState({ ...EMPTY_SCRIPT(), ...(template.script || {}) });
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Seed templates: content is only editable by staff. Non-staff still see the
  // dialog to change visibility (backend enforces the split too).
  const seed = !!template.is_seed;
  const contentEditable = !seed || !!isStaff;
  const contentLocked = !contentEditable;

  const upd = (patch) => setScript((prev) => ({ ...prev, ...patch }));
  const save = async () => {
    if (contentEditable && !name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = contentLocked
        ? { visibility }   // visibility-only path (non-staff on a seed)
        : {
            name: name.trim(),
            description: description.trim(),
            script,
            tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
            calendly_link: calendlyLink.trim(),
            visibility,
          };
      await api.patch(`/outbound/script-templates/${template.template_id}`, payload);
      toast.success(seed && contentEditable ? "Built-in template updated" : "Template updated");
      await onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const revert = async () => {
    if (!window.confirm("Restore this built-in template to its shipped default? All customisations will be lost.")) return;
    setReverting(true);
    try {
      const { data } = await api.post(`/outbound/script-templates/${template.template_id}/revert-seed`);
      // Repopulate the dialog from the restored defaults so the user can see the change take effect.
      setName(data.name || "");
      setDescription(data.description || "");
      setTagsStr((data.tags || []).join(", "));
      setCalendlyLink(data.calendly_link || "");
      setVisibility(data.visibility || "staff");
      setScript({ ...EMPTY_SCRIPT(), ...(data.script || {}) });
      toast.success("Reverted to shipped default");
      await onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Revert failed"); }
    finally { setReverting(false); }
  };

  const titleText = seed
    ? (contentEditable ? "Edit built-in template" : "Set visibility on built-in template")
    : "Edit template";
  const descriptionText = seed
    ? (contentEditable
        ? "You're editing a built-in template. Your changes persist across backend restarts and stay visible everywhere this template is used. Click 'Revert to built-in' to restore the shipped default."
        : "Built-in templates are content-locked so future refreshes stay authoritative. You can still change who in your organisation can see and use this template. Use 'Copy to my org' if you need to customise the script.")
    : "Changes save immediately for everyone in your organisation. Campaigns already using this template as a starting point are unaffected — their scripts are copies.";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-outbound-template-edit-dialog">
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
          <DialogDescription>{descriptionText}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Row2>
            <Field label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={contentLocked}
                data-testid="ai-outbound-template-edit-name" />
            </Field>
            <Field label="Tags (comma-separated)">
              <Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} disabled={contentLocked}
                data-testid="ai-outbound-template-edit-tags" />
            </Field>
          </Row2>
          <Field label="Description">
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={contentLocked}
              data-testid="ai-outbound-template-edit-desc" />
          </Field>
          <Field label={<LabelWithHint text="Calendly link (attached to this template)" testId="ai-outbound-template-edit-calendly-info"
            hint="When a campaign loads this template AND the campaign doesn't already have its own Calendly link, this URL is copied over automatically. Leave blank if the booking link should vary per-campaign." />}>
            <Input value={calendlyLink} onChange={(e) => setCalendlyLink(e.target.value)} disabled={contentLocked}
              placeholder="https://calendly.com/…"
              data-testid="ai-outbound-template-edit-calendly" />
          </Field>
          <Field label={<LabelWithHint text="Visibility" testId="ai-outbound-template-edit-visibility-info"
            hint="Who in your organisation can see this template in the picker. Staff = internal only. Staff + Partners = your channel partners can also fork it. Everyone = customers can too." />}>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="ai-outbound-template-edit-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff only</SelectItem>
                <SelectItem value="staff_partners">Staff + Partners</SelectItem>
                <SelectItem value="staff_partners_customers">Everyone (Staff + Partners + Customers)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Opening (hook)">
            <Textarea rows={2} value={script.opening} onChange={(e) => upd({ opening: e.target.value })} disabled={contentLocked} data-testid="ai-outbound-template-edit-opening" />
          </Field>
          <Field label="Value prop">
            <Textarea rows={3} value={script.value_prop} onChange={(e) => upd({ value_prop: e.target.value })} disabled={contentLocked} data-testid="ai-outbound-template-edit-valueprop" />
          </Field>
          <Field label="Qualifying question">
            <Textarea rows={2} value={script.qualifying_question} onChange={(e) => upd({ qualifying_question: e.target.value })} disabled={contentLocked} data-testid="ai-outbound-template-edit-qualifying" />
          </Field>
          <Field label="Value bullets (one per line)">
            <Textarea rows={4} value={(script.value_bullets || []).join("\n")}
              onChange={(e) => upd({ value_bullets: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
              data-testid="ai-outbound-template-edit-bullets" disabled={contentLocked} />
          </Field>
          <Field label="Call to action">
            <Textarea rows={2} value={script.call_to_action} onChange={(e) => upd({ call_to_action: e.target.value })} disabled={contentLocked} data-testid="ai-outbound-template-edit-cta" />
          </Field>
          <Field label="Objections (Trigger⇧Enter Response — one pair per double-newline block)">
            <Textarea rows={4} placeholder='Trigger: Not interested&#10;Response: Totally understand — would you like a one-pager?'
              value={(script.objections || []).map((o) => `Trigger: ${o.trigger || ""}\nResponse: ${o.response || ""}`).join("\n\n")}
              onChange={(e) => {
                const blocks = e.target.value.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
                const objs = blocks.map((blk) => {
                  const trig = /Trigger:\s*(.*)/i.exec(blk)?.[1]?.trim() || "";
                  const resp = /Response:\s*([\s\S]*)/i.exec(blk)?.[1]?.trim() || "";
                  return { trigger: trig, response: resp };
                }).filter((o) => o.trigger || o.response);
                upd({ objections: objs });
              }}
              data-testid="ai-outbound-template-edit-objections" disabled={contentLocked} />
          </Field>
          <Field label="Close">
            <Textarea rows={2} value={script.close} onChange={(e) => upd({ close: e.target.value })} disabled={contentLocked} data-testid="ai-outbound-template-edit-close" />
          </Field>
        </div>
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div>
            {seed && contentEditable && template.content_overridden && (
              <Button variant="outline" onClick={revert} disabled={reverting || saving}
                data-testid="ai-outbound-template-edit-revert"
                className="text-slate-600">
                {reverting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Revert to built-in
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="ai-outbound-template-edit-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || (contentEditable && !name.trim())}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="ai-outbound-template-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
